import { useState } from 'react';
import { ChevronDown, ChevronUp, User, UserX, Bot, MousePointerClick } from 'lucide-react';

export function SearchLogsTable({ rows, t, page, limit, total, onPage }) {
  const [open, setOpen] = useState(null);
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="search-logs-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-white/[0.03] text-white/50">
            <tr>
              <th className="px-3 py-2 text-start">{t('الوقت (دبي)', 'Time (Dubai)')}</th>
              <th className="px-3 py-2 text-start">{t('كلمة البحث', 'Query')}</th>
              <th className="px-3 py-2 text-center">{t('نتائج', 'Results')}</th>
              <th className="px-3 py-2 text-start">{t('العميل', 'Customer')}</th>
              <th className="px-3 py-2 text-start">{t('الموقع', 'Location')}</th>
              <th className="px-3 py-2 text-start">{t('الجهاز', 'Device')}</th>
              <th className="px-3 py-2 text-start">{t('الصفحة', 'Page')}</th>
              <th className="px-3 py-2 text-center">{t('تفاعل', 'Actions')}</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-white/30" data-testid="search-logs-empty">
                {t('لا توجد سجلات في هذه الفترة بعد — تمتلئ تلقائياً عندما يبحث العملاء في منصة العملاء', 'No logs for this period yet — fills automatically as customers search')}
              </td></tr>
            )}
            {rows.map((r) => (
              <RowPair key={r.id} r={r} t={t} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-3 py-2 border-t border-white/5 text-[11px] text-white/50">
        <span>{t(`الإجمالي ${total}`, `Total ${total}`)}</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="px-2 py-1 rounded bg-white/5 disabled:opacity-30" data-testid="search-logs-prev">{t('السابق', 'Prev')}</button>
          <span dir="ltr">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => onPage(page + 1)} className="px-2 py-1 rounded bg-white/5 disabled:opacity-30" data-testid="search-logs-next">{t('التالي', 'Next')}</button>
        </div>
      </div>
    </div>
  );
}

function RowPair({ r, t, open, onToggle }) {
  const zero = r.results === 0;
  return (
    <>
      <tr className={`border-t border-white/5 hover:bg-white/[0.02] ${zero ? 'bg-red-500/[0.04]' : ''}`} data-testid={`search-log-row-${r.id}`}>
        <td className="px-3 py-2 text-white/60 font-mono whitespace-nowrap" dir="ltr">{r.dubai_time || '—'}</td>
        <td className="px-3 py-2 text-white font-semibold max-w-[260px] truncate" dir="auto" title={r.query}>{r.query || '—'}</td>
        <td className={`px-3 py-2 text-center font-mono ${zero ? 'text-red-300' : 'text-white/70'}`}>{r.results ?? '—'}</td>
        <td className="px-3 py-2">
          {r.customer_id ? (
            <span className="inline-flex items-center gap-1 text-emerald-300"><User className="w-3 h-3" />
              <span className="truncate max-w-[160px]">{r.customer?.name || r.customer?.email || r.customer_id}</span></span>
          ) : (
            <span className="inline-flex items-center gap-1 text-white/40"><UserX className="w-3 h-3" />{t('زائر', 'Guest')}</span>
          )}
        </td>
        <td className="px-3 py-2 text-white/70 whitespace-nowrap">{[r.country, r.city].filter(Boolean).join(' · ') || '—'}</td>
        <td className="px-3 py-2 text-white/60 whitespace-nowrap">{[r.device, r.os, r.browser].filter(Boolean).join(' · ') || '—'}</td>
        <td className="px-3 py-2 text-white/50 max-w-[160px] truncate" dir="ltr" title={r.page}>{r.page || '—'}{r.language ? ` · ${r.language}` : ''}</td>
        <td className="px-3 py-2 text-center">
          <span className="inline-flex items-center gap-2">
            {r.clicked != null && <MousePointerClick className="w-3.5 h-3.5 text-sky-300" title={t('فتح نتيجة', 'Opened a result')} />}
            {r.ai && <Bot className="w-3.5 h-3.5 text-[#d4af37]" title={t('استعان بالمساعد الذكي', 'Asked the AI assistant')} />}
            {r.clicked == null && !r.ai && <span className="text-white/20">—</span>}
          </span>
        </td>
        <td className="px-2 py-2">
          <button onClick={onToggle} className="text-white/40 hover:text-white" data-testid={`search-log-expand-${r.id}`}>
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="bg-black/40">
          <td colSpan={9} className="px-4 py-3 text-[11px] text-white/70">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Kv k={t('المعرّف', 'ID')} v={r.id} /><Kv k={t('رقم العميل', 'Customer ID')} v={r.customer_id || '—'} />
              <Kv k={t('البريد', 'Email')} v={r.customer?.email || '—'} /><Kv k={t('الهاتف', 'Phone')} v={r.customer?.phone || '—'} />
              <Kv k={t('القسم', 'Section')} v={r.section || '—'} /><Kv k={t('النص المطبّع', 'Normalized')} v={r.normalized || '—'} />
              <Kv k={t('الحدث', 'Event')} v={r.event || 'search'} /><Kv k="UTC" v={r.created_at || '—'} />
            </div>
            {r.clicked != null && (
              <pre className="mt-2 p-2 rounded bg-white/5 text-[10px] text-sky-200 overflow-x-auto" dir="ltr">
                {typeof r.clicked === 'object' ? JSON.stringify(r.clicked, null, 1) : String(r.clicked)}
              </pre>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Kv({ k, v }) {
  return <div><span className="text-white/40">{k}: </span><span className="font-mono break-all" dir="ltr">{String(v)}</span></div>;
}
